// src/components/consultorio/OrcamentosTab.js
import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useAlertModal } from '../../AlertModalContext';
import OrcamentoModal from './OrcamentoModal';

const STATUS_META = {
    // ✅ chaves com inicial maiúscula — igual ao que o backend retorna
    Pendente:  { bg: '#fef9c3', color: '#854d0e', label: 'Pendente',  dot: '#f59e0b' },
    Aprovado:  { bg: '#dcfce7', color: '#16a34a', label: 'Aprovado',  dot: '#22c55e' },
    Recusado:  { bg: '#fef2f2', color: '#dc2626', label: 'Recusado',  dot: '#ef4444' },
    Expirado:  { bg: '#f3f4f6', color: '#6b7280', label: 'Expirado',  dot: '#9ca3af' },
};

// fallback para status desconhecido
const STATUS_DEFAULT = { bg: '#f3f4f6', color: '#6b7280', label: 'Pendente', dot: '#9ca3af' };

const OrcamentosTab = ({ pacienteId, servicos = [] }) => {
    const { showAlertModal } = useAlertModal();
    const [orcamentos, setOrcamentos]       = useState([]);
    const [loading, setLoading]             = useState(false);
    const [showModal, setShowModal]         = useState(false);
    const [orcamentoEdit, setOrcamentoEdit] = useState(null);
    const [expandedId, setExpandedId]       = useState(null);

    const fetchOrcamentos = useCallback(async () => {
        if (!pacienteId) return;
        setLoading(true);
        try {
            const res = await axios.get(`/api/orcamentos?paciente_id=${pacienteId}`);

            // ✅ A API pode retornar array direto OU objeto paginado { data: [...] }
            const payload = res.data.orcamentos;
            if (Array.isArray(payload)) {
                setOrcamentos(payload);
            } else if (payload && Array.isArray(payload.data)) {
                setOrcamentos(payload.data);
            } else {
                setOrcamentos([]);
            }
        } catch {
            setOrcamentos([]);
        } finally {
            setLoading(false);
        }
    }, [pacienteId]);

    useEffect(() => { fetchOrcamentos(); }, [fetchOrcamentos]);

    const handleSaved = (orcamento) => {
        setOrcamentos(prev => {
            const idx = prev.findIndex(o => o.id === orcamento.id);
            if (idx >= 0) {
                const novo = [...prev];
                novo[idx] = orcamento;
                return novo;
            }
            return [orcamento, ...prev];
        });
    };

    const handleDelete = async (id) => {
        showAlertModal(
            'Confirmar Exclusão',
            'Deseja excluir este orçamento?',
            'warning',
            async () => {
                try {
                    await axios.delete(`/api/orcamentos/${id}`);
                    setOrcamentos(prev => prev.filter(o => o.id !== id));
                } catch {
                    showAlertModal('Erro', 'Não foi possível excluir o orçamento.', 'danger');
                }
            }
        );
    };

    const fmt = (val) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(val) || 0);

    const fmtDate = (d) =>
        d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—';

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '48px', color: '#6b7280' }}>
            <Spinner animation="border" size="sm" variant="primary" />
            <span style={{ fontSize: 'calc(13px * var(--font-scale))' }}>Carregando orçamentos...</span>
        </div>
    );

    return (
        <div>
            {/* Header da aba */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                    <div style={{ fontSize: 'calc(14px * var(--font-scale))', fontWeight: '700', color: '#111827' }}>
                        Orçamentos
                    </div>
                    <div style={{ fontSize: 'calc(12px * var(--font-scale))', color: '#9ca3af', marginTop: '2px' }}>
                        {orcamentos.length} orçamento{orcamentos.length !== 1 ? 's' : ''}
                    </div>
                </div>
                <button
                    onClick={() => { setOrcamentoEdit(null); setShowModal(true); }}
                    style={{
                        padding: '8px 14px', borderRadius: '8px', border: 'none',
                        background: 'linear-gradient(135deg, #0f172a, #1e40af)',
                        color: '#fff', cursor: 'pointer',
                        fontSize: 'calc(12px * var(--font-scale))', fontWeight: '600',
                        display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit',
                    }}
                >
                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Novo Orçamento
                </button>
            </div>

            {/* Lista vazia */}
            {orcamentos.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '48px 24px',
                    backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb',
                }}>
                    <svg width="40" height="40" fill="none" stroke="#d1d5db" viewBox="0 0 24 24"
                        style={{ display: 'block', margin: '0 auto 12px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p style={{ margin: 0, fontSize: 'calc(13px * var(--font-scale))', color: '#9ca3af' }}>
                        Nenhum orçamento cadastrado.
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 'calc(12px * var(--font-scale))', color: '#d1d5db' }}>
                        Clique em "Novo Orçamento" para começar.
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {orcamentos.map(orc => {
                        // ✅ STATUS_META com chave exata do backend, com fallback seguro
                        const meta   = STATUS_META[orc.status] ?? STATUS_DEFAULT;
                        const isOpen = expandedId === orc.id;

                        return (
                            <div key={orc.id} style={{
                                backgroundColor: '#fff', borderRadius: '12px',
                                border: '1px solid #e5e7eb', overflow: 'hidden',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}>
                                {/* Header do card */}
                                <div
                                    style={{
                                        padding: '12px 16px', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '12px',
                                        backgroundColor: isOpen ? '#f8fafc' : '#fff',
                                        borderBottom: isOpen ? '1px solid #f3f4f6' : 'none',
                                        transition: 'background 0.15s',
                                    }}
                                    onClick={() => setExpandedId(isOpen ? null : orc.id)}
                                >
                                    {/* Dot status */}
                                    <div style={{
                                        width: '10px', height: '10px', borderRadius: '50%',
                                        backgroundColor: meta.dot, flexShrink: 0,
                                    }} />

                                    {/* Título + data */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 'calc(13px * var(--font-scale))', fontWeight: '600',
                                            color: '#111827', overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {orc.titulo || `Orçamento #${orc.numero}`}
                                        </div>
                                        <div style={{ fontSize: 'calc(11px * var(--font-scale))', color: '#9ca3af', marginTop: '2px' }}>
                                            Criado em {fmtDate(orc.created_at?.split('T')[0])}
                                            {orc.validade && ` · Válido até ${fmtDate(orc.validade?.split('T')[0])}`}
                                        </div>
                                    </div>

                                    {/* Valor total */}
                                    <div style={{
                                        fontSize: 'calc(14px * var(--font-scale))', fontWeight: '700',
                                        color: '#111827', flexShrink: 0,
                                    }}>
                                        {fmt(orc.valor_total)}
                                    </div>

                                    {/* Badge status */}
                                    <span style={{
                                        fontSize: 'calc(11px * var(--font-scale))', fontWeight: '600',
                                        padding: '3px 10px', borderRadius: '999px', flexShrink: 0,
                                        backgroundColor: meta.bg, color: meta.color,
                                    }}>
                                        {meta.label}
                                    </span>

                                    {/* Chevron */}
                                    <svg
                                        width="14" height="14" fill="none" stroke="#9ca3af" viewBox="0 0 24 24"
                                        style={{ flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>

                                {/* Detalhes expandidos */}
                                {isOpen && (
                                    <div style={{ padding: '14px 16px' }}>

                                        {/* Número do orçamento */}
                                        {orc.numero && (
                                            <div style={{
                                                fontSize: 'calc(11px * var(--font-scale))', color: '#9ca3af',
                                                marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px',
                                            }}>
                                                <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                                </svg>
                                                {orc.numero}
                                                {orc.criadoPor?.nome && (
                                                    <span style={{ marginLeft: '8px' }}>
                                                        · por <strong>{orc.criadoPor.nome}</strong>
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Itens */}
                                        {(orc.itens || []).length > 0 && (
                                            <div style={{ marginBottom: '12px' }}>
                                                <div style={{
                                                    fontSize: 'calc(10px * var(--font-scale))', fontWeight: '700',
                                                    color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px',
                                                    marginBottom: '8px',
                                                }}>
                                                    Serviços
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {orc.itens.map((item, idx) => (
                                                        <div key={idx} style={{
                                                            display: 'flex', justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '8px 12px', borderRadius: '8px',
                                                            backgroundColor: '#f9fafb', border: '1px solid #f3f4f6',
                                                        }}>
                                                            <div>
                                                                <div style={{ fontSize: 'calc(13px * var(--font-scale))', fontWeight: '500', color: '#374151' }}>
                                                                    {item.servico?.nome || `Serviço #${item.servico_id}`}
                                                                </div>
                                                                <div style={{ fontSize: 'calc(11px * var(--font-scale))', color: '#9ca3af' }}>
                                                                    {item.quantidade}x · {fmt(item.valor_unitario)} cada
                                                                </div>
                                                            </div>
                                                            <div style={{ fontSize: 'calc(13px * var(--font-scale))', fontWeight: '700', color: '#111827' }}>
                                                                {fmt(parseFloat(item.valor_unitario || 0) * parseInt(item.quantidade || 1))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Totais */}
                                                <div style={{
                                                    marginTop: '10px', padding: '10px 12px',
                                                    borderRadius: '8px', backgroundColor: '#f0fdf4',
                                                    border: '1px solid #bbf7d0',
                                                }}>
                                                    {/* ✅ campo correto: valor_desconto (não desconto) */}
                                                    {parseFloat(orc.valor_desconto) > 0 && (
                                                        <div style={{
                                                            display: 'flex', justifyContent: 'space-between',
                                                            fontSize: 'calc(12px * var(--font-scale))',
                                                            color: '#ef4444', marginBottom: '4px',
                                                        }}>
                                                            <span>Desconto:</span>
                                                            <span>- {fmt(orc.valor_desconto)}</span>
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        display: 'flex', justifyContent: 'space-between',
                                                        fontSize: 'calc(14px * var(--font-scale))',
                                                        fontWeight: '800', color: '#16a34a',
                                                    }}>
                                                        <span>Total:</span>
                                                        <span>{fmt(orc.valor_total)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Observações */}
                                        {orc.observacoes && (
                                            <div style={{
                                                padding: '10px 12px', borderRadius: '8px',
                                                backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                                                fontSize: 'calc(12px * var(--font-scale))', color: '#92400e',
                                                marginBottom: '12px', lineHeight: '1.5',
                                            }}>
                                                📝 {orc.observacoes}
                                            </div>
                                        )}

                                        {/* Ações */}
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                            {/* Editar só se Pendente */}
                                            {orc.status === 'Pendente' && (
                                                <button
                                                    onClick={() => { setOrcamentoEdit(orc); setShowModal(true); }}
                                                    style={{
                                                        padding: '6px 14px', borderRadius: '7px',
                                                        border: '1px solid #bfdbfe', backgroundColor: '#eff6ff',
                                                        color: '#2563eb', cursor: 'pointer',
                                                        fontSize: 'calc(12px * var(--font-scale))', fontWeight: '600',
                                                        display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'inherit',
                                                    }}
                                                >
                                                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    Editar
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDelete(orc.id)}
                                                style={{
                                                    padding: '6px 14px', borderRadius: '7px',
                                                    border: '1px solid #fecaca', backgroundColor: '#fef2f2',
                                                    color: '#dc2626', cursor: 'pointer',
                                                    fontSize: 'calc(12px * var(--font-scale))', fontWeight: '600',
                                                    display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'inherit',
                                                }}
                                            >
                                                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                Excluir
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal */}
            <OrcamentoModal
                show={showModal}
                onHide={() => setShowModal(false)}
                paciente={{ id: pacienteId }}
                orcamento={orcamentoEdit}
                servicos={servicos}
                onSaved={handleSaved}
            />
        </div>
    );
};

export default OrcamentosTab;
